import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, lastValueFrom, Observable, ReplaySubject } from 'rxjs'
import { Actor, AppInfo, ILSStructure, PhaseStructure } from '@monorepo/next-lab'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import {
  Content,
  ContentWithConfiguration,
  GroupsInfo,
  IlsDescription,
  IlsService,
  IlsWithContent,
  PhaseWithContent,
  Student,
} from '@monorepo/golab-generated'
import { IlsModelPartsService } from '@monorepo/angular-common'

export enum IlsStatus {
  NotLoaded = 'NotLoaded',
  Loading = 'Loading',
  Loaded = 'Loaded',
  Error = 'Error',
}

const twenteAppsUrlParts = ['.utwente.nl/', '//apps.dev.graasp.eu/']
const proxyUrlPart = '/server/proxy/'

@Injectable({
  providedIn: 'root',
})
export class IlsModelService extends IlsModelPartsService {
  private readonly ilsService = inject(IlsService)
  private readonly router = inject(Router)
  private readonly translateService = inject(TranslateService)

  readonly currentPhaseId$ = new BehaviorSubject<string | null>(null)
  readonly ilsLoaded$ = new BehaviorSubject<boolean>(false)
  readonly ilsStatus$ = new BehaviorSubject<IlsStatus>(IlsStatus.NotLoaded)
  readonly serverPreTitle: string
  readonly url = ''

  constructor() {
    super()
    this.serverPreTitle = this.getSeverPreTitle()
  }

  private _errorMessage = 'en'

  get errorMessage(): string {
    return this._errorMessage
  }

  private _phaseList: PhaseWithContent[] = []

  get phaseList(): PhaseWithContent[] {
    return this._phaseList
  }

  private _ilsStructure: ILSStructure | undefined

  get ilsStructure(): ILSStructure | undefined {
    return this._ilsStructure
  }

  override getIlsStructure(): ILSStructure {
    return this._ilsStructure!
  }

  private _publishedAt: Date | null = null

  get publishedAt(): Date | null {
    return this._publishedAt
  }

  private _publishedId: string | null = null

  get publishedId(): string | null {
    return this._publishedId
  }

  get published(): boolean {
    if (!this._publishedAt !== !this._publishedId) {
      console.error('Inconsistency in published data:', this._publishedAt, this._publishedId)
    }
    return !!this._publishedAt && !!this._publishedId
  }

  get currentPhaseId(): string {
    return this.currentPhaseId$.value ?? ''
  }

  set currentPhaseId(id: string) {
    const currentPhase = this._phaseList.find((phase) => phase._id === id)
    if (!currentPhase) {
      throw new Error(`Phase id ${id} not found`)
    }
    this.currentPhaseId$.next(id)
  }

  get currentPhase(): PhaseWithContent {
    const currentPhase = this._phaseList.find((phase) => phase._id === this.currentPhaseId)
    if (!currentPhase) {
      throw new Error(`Phase id ${this.currentPhaseId} not found`)
    }
    return currentPhase
  }

  get currentPhaseIndex(): number {
    return this._phaseList.findIndex((phase) => phase._id === this.currentPhaseId)
  }

  get hasNextPhase(): boolean {
    const index = this.currentPhaseIndex
    return index >= 0 && index < this._phaseList.length - 1
  }

  get hasPreviousPhase(): boolean {
    return this.currentPhaseIndex > 0
  }

  goToNextPhase(): void {
    const index = this.currentPhaseIndex
    if (index >= 0 && index < this._phaseList.length - 1) {
      this.currentPhaseId = this._phaseList[index + 1]._id
    }
  }

  goToPreviousPhase(): void {
    const index = this.currentPhaseIndex
    if (index > 0) {
      this.currentPhaseId = this._phaseList[index - 1]._id
    }
  }

  loadInfoFromIlsDescription(ilsId: IlsDescription) {
    this._ilsId = ilsId._id
    this.language$.next(ilsId.language)
    this.title$.next(ilsId.title)
    // this._description = ilsId.description
    this.learningAnalyticsEnabled$.next(ilsId.learningAnalyticsEnabled)
    this.enableNavigationButtons$.next(ilsId.enableNavigationButtons ?? false)
    this._publishedAt = ilsId.publishedAt ? new Date(ilsId.publishedAt) : null
    this._publishedId = ilsId.publishedId
    this.published$.next(this.published)
  }

  initializeIls(ilsId: string) {
    // console.log('initializeIls', ilsId)
    this.ilsStatus$.next(IlsStatus.Loading)
    this.LoadILS(ilsId).subscribe((ilsModel) => {
      if (ilsModel) {
        // console.log('ils model loaded')
        this.parseIlsModel(ilsModel)
        this._errorMessage = this.validateIls()
        if (this._errorMessage) {
          this.ilsStatus$.next(IlsStatus.Error)
          return
        }
        this.currentPhaseId = this._phaseList[0]?._id
        this.phaseListVisible$.next(true)
        this.globalContentVisible$.next(this.globalContentList$.value.length > 0)
        this.ilsLoaded$.next(true)
        this.ilsStatus$.next(IlsStatus.Loaded)
      }
    })
  }

  loggedOut() {
    this.currentPhaseId$.next(null)
    this.ilsLoaded$.next(false)
    this.ilsStatus$.next(IlsStatus.NotLoaded)
  }

  isTwenteApp(content: Content | undefined): boolean {
    if (content?.labProvider) {
      return content.labProvider.toLowerCase() === 'twente'
    }
    if (content?.url) {
      const url = content.url.toLowerCase()
      if (url.includes(proxyUrlPart)) {
        return false
      }
      return twenteAppsUrlParts.some((urlPart) => url.includes(urlPart))
    }
    return false
  }

  override async getAllStudents(): Promise<Student[]> {
    if (!this.ilsLoaded$.value || this.published) {
      return []
    }
    const students = await lastValueFrom(
      this.ilsService.ilsControllerGetAllStudents({
        ilsId: this.ilsId,
      }),
    )
    return students
  }

  override async getGroupsInfo(): Promise<GroupsInfo> {
    const groupsInfo = await lastValueFrom(
      this.ilsService.ilsControllerGetAllGroups({
        ilsId: this.ilsId,
      }),
    )
    return groupsInfo
  }

  override async getStudents(): Promise<Actor[]> {
    if (!this.ilsLoaded$.value || this.published) {
      return []
    }
    const students = await lastValueFrom(
      this.ilsService.ilsControllerGetAllStudents({
        ilsId: this.ilsId,
      }),
    )
    return students.map((student) => this.studentToActor(student))
  }

  override async createStudent(nickname: string): Promise<Actor> {
    if (!this.ilsLoaded$.value) {
      return Promise.reject()
    } else {
      const student = await lastValueFrom(
        this.ilsService.ilsControllerCreateStudent({
          ilsId: this.ilsId,
          body: {
            nickname,
            password: '',
          },
        }),
      )
      return this.studentToActor(student)
    }
  }

  private getSeverPreTitle(): string {
    const hostName = window.location.hostname
    const hostNameParts = hostName.split('.')
    const mainHostName = hostNameParts[0]
    if (mainHostName === 'go-lab') {
      return ''
    }
    if (mainHostName === 'go-lab-develop') {
      return 'develop:'
    }
    return `${mainHostName}: `
  }

  private parseIlsModel(ilsModel: IlsWithContent): void {
    this._ilsId = ilsModel._id
    this.language$.next(ilsModel.language)
    this.translateService.use(this.language$.value)
    this.title$.next(ilsModel.title)
    this._phaseList = ilsModel.phaseList
    this.phaseList$.next(this._phaseList)
    this.globalContentList$.next(ilsModel.contentList)
    this.learningAnalyticsEnabled$.next(ilsModel.learningAnalyticsEnabled)
    this.enableNavigationButtons$.next(ilsModel.enableNavigationButtons ?? false)
    this._publishedAt = ilsModel.publishedAt ? new Date(ilsModel.publishedAt) : null
    this._publishedId = ilsModel.publishedId
    this.published$.next(this.published)
    this._ilsStructure = this.createIlsStructure()
    // console.log('ilsStructure', this._ilsStructure)
    this.removeInvisibleElements()
  }

  private createIlsStructure(): ILSStructure {
    const ilsStructure: ILSStructure = {
      id: this._ilsId,
      displayName: this.title$.value,
      url: '',
      ilsId: this._ilsId,
      apps: this.contentListToAppInfoList(this.globalContentList$.value),
      phases: this._phaseList.map((phase) => this.createPhaseStructure(phase)),
      ilsHasAngeLA: this.learningAnalyticsEnabled$.value,
    }
    return ilsStructure
  }

  private contentListToAppInfoList(contentList: ContentWithConfiguration[]): AppInfo[] {
    return contentList
      .filter((content) => this.contentIsTwenteApp(content))
      .map((content) => this.contentToAppInfo(content))
  }

  private contentIsTwenteApp(content: Content): boolean {
    switch (content.contentType) {
      case 'app':
      case 'customApp':
      case 'lab':
        return true
      default:
        return false
    }
  }

  private contentToAppInfo(content: ContentWithConfiguration): AppInfo {
    return {
      displayName: content.title,
      id: content._id,
      appType: 'WidgetGadget',
      itemType: 'Application',
      url: content.url ? content.url : '',
      hidden: content.notForStudents,
      configuration: content.configuration,
    }
  }

  private createPhaseStructure(phase: PhaseWithContent): PhaseStructure {
    return {
      id: phase._id,
      displayName: phase.title,
      type: 'User defined',
      visibilityLevel: 'private',
      apps: this.contentListToAppInfoList(phase.contentList),
      hidden: phase.notForStudents,
    }
  }

  private removeInvisibleElements() {
    this._phaseList = this.filterVisible(this._phaseList)
    this._phaseList.forEach((phase) => {
      phase.contentList = this.filterVisible(phase.contentList)
    })
    this.globalContentList$.next(this.filterVisible(this.globalContentList$.value))
  }

  private filterVisible<T extends { notForStudents: boolean }>(items: T[]): T[] {
    return items.filter((item) => !item.notForStudents)
  }

  private validateIls(): string {
    if (this._phaseList.length === 0) {
      return 'noPhases'
    }
    return ''
  }

  private LoadILS(ilsId: string): Observable<IlsWithContent | null> {
    const ils$ = new ReplaySubject<IlsWithContent | null>()
    this.ilsService.ilsControllerFindIlsWithContent({ ilsId }).subscribe({
      next: (ilsWithContent) => {
        // console.log('ilsWithContent', ilsWithContent)
        ils$.next(ilsWithContent)
      },
      error: (error) => {
        console.log('error', error)
        this.router.navigate([''])
        ils$.next(null)
      },
    })
    return ils$
  }

  private studentToActor(student: Student): Actor {
    return {
      id: student._id,
      displayName: student.nickname,
      objectType: 'graasp_student',
    }
  }
}
